import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export interface Scholarship {
  id: string;
  title: string;
  provider: string;
  amount: string;
  eligibility: string;
  deadline: string;
  status: 'active' | 'inactive';
  description?: string;
  applicationLink?: string;
  category?: 'merit' | 'minority' | 'need-based' | 'sports' | 'arts' | 'other';
  requirements?: string[];
  contact?: {
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
  };
}

interface ScholarshipContextType {
  scholarships: Scholarship[];
  addScholarship: (scholarship: Omit<Scholarship, 'id'>) => void;
  updateScholarship: (id: string, scholarship: Partial<Scholarship>) => void;
  deleteScholarship: (id: string) => void;
  getScholarshipById: (id: string) => Scholarship | undefined;
  getScholarshipsByCategory: (category: string) => Scholarship[];
}

const ScholarshipContext = createContext<ScholarshipContextType | undefined>(undefined);

const STORAGE_KEY = 'edHuntWeb_scholarships';

export const ScholarshipProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);

  // Load scholarships from localStorage on component mount
  useEffect(() => {
    const savedScholarships = localStorage.getItem(STORAGE_KEY);
    if (savedScholarships) {
      try {
        const parsedScholarships = JSON.parse(savedScholarships);
        setScholarships(parsedScholarships);
      } catch (error) {
        console.error('Error parsing saved scholarships:', error);
        // If parsing fails, initialize with default scholarships
        initializeDefaultScholarships();
      }
    } else {
      // Initialize with default scholarships if none exist
      initializeDefaultScholarships();
    }
  }, []);

  // Save scholarships to localStorage whenever scholarships change
  useEffect(() => {
    if (scholarships.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scholarships));
    }
  }, [scholarships]);

  const initializeDefaultScholarships = () => {
    const defaultScholarships: Scholarship[] = [
      {
        id: '1',
        title: 'Merit Scholarship 2024',
        provider: 'Government of India',
        amount: '₹50,000',
        eligibility: '85%+ in 12th grade',
        deadline: '2024-12-31',
        status: 'active',
        category: 'merit',
        description: 'Merit-based scholarship for outstanding academic performance in 12th grade examinations.',
        applicationLink: 'https://scholarships.gov.in/merit',
        requirements: ['12th grade marks above 85%', 'Valid Aadhaar card', 'Bank account details', 'Income certificate'],
        contact: {
          phone: '+91-11-2345-6789',
          email: 'merit@scholarships.gov.in',
          website: 'https://scholarships.gov.in',
          address: 'Ministry of Education, Shastri Bhawan, New Delhi'
        }
      },
      {
        id: '2',
        title: 'Minority Scholarship Program',
        provider: 'Minority Affairs Ministry',
        amount: '₹75,000',
        eligibility: 'Minority community students',
        deadline: '2024-11-15',
        status: 'active',
        category: 'minority',
        description: 'Financial assistance for students from minority communities pursuing higher education.',
        applicationLink: 'https://minorityaffairs.gov.in/scholarship',
        requirements: ['Minority community certificate', 'Income certificate', 'Academic transcripts', 'Admission proof'],
        contact: {
          phone: '+91-11-2300-4567',
          email: 'scholarship@minorityaffairs.gov.in',
          website: 'https://minorityaffairs.gov.in',
          address: 'Ministry of Minority Affairs, Patiala House Annexe, New Delhi'
        }
      },
      {
        id: '3',
        title: 'Post-Matric Scholarship',
        provider: 'State Government',
        amount: '₹25,000',
        eligibility: 'SC/ST/OBC students',
        deadline: '2024-10-30',
        status: 'active',
        category: 'minority',
        description: 'Post-matriculation scholarship for students from scheduled castes, scheduled tribes, and other backward classes.',
        applicationLink: 'https://state.gov.in/postmatric',
        requirements: ['Caste certificate', 'Income certificate', 'Previous year marks', 'College admission letter'],
        contact: {
          phone: '+91-11-2400-5678',
          email: 'postmatric@state.gov.in',
          website: 'https://state.gov.in/scholarship',
          address: 'State Scholarship Board, Sector-5, New Delhi'
        }
      },
      {
        id: '4',
        title: 'Sports Excellence Scholarship',
        provider: 'Sports Authority of India',
        amount: '₹1,00,000',
        eligibility: 'State/National level sports achievers',
        deadline: '2024-09-15',
        status: 'active',
        category: 'sports',
        description: 'Scholarship for talented athletes who have represented their state or country in sports competitions.',
        applicationLink: 'https://sai.gov.in/sports-scholarship',
        requirements: ['Sports achievement certificates', 'Coach recommendation', 'Medical fitness certificate', 'Academic records'],
        contact: {
          phone: '+91-11-2600-7890',
          email: 'sports@sai.gov.in',
          website: 'https://sai.gov.in',
          address: 'Sports Authority of India, JLN Stadium, New Delhi'
        }
      }
    ];
    setScholarships(defaultScholarships);
  };

  const addScholarship = (scholarshipData: Omit<Scholarship, 'id'>) => {
    const newScholarship: Scholarship = {
      ...scholarshipData,
      id: Date.now().toString(), // Simple ID generation
    };
    setScholarships(prev => [...prev, newScholarship]);
  };

  const updateScholarship = (id: string, scholarshipData: Partial<Scholarship>) => {
    setScholarships(prev => 
      prev.map(scholarship => 
        scholarship.id === id 
          ? { ...scholarship, ...scholarshipData, id } // Ensure ID is preserved
          : scholarship
      )
    );
  };

  const deleteScholarship = (id: string) => {
    setScholarships(prev => prev.filter(scholarship => scholarship.id !== id));
  };

  const getScholarshipById = (id: string): Scholarship | undefined => {
    return scholarships.find(scholarship => scholarship.id === id);
  };

  const getScholarshipsByCategory = (category: string): Scholarship[] => {
    return scholarships.filter(scholarship => scholarship.category === category);
  };

  return (
    <ScholarshipContext.Provider value={{
      scholarships,
      addScholarship,
      updateScholarship,
      deleteScholarship,
      getScholarshipById,
      getScholarshipsByCategory
    }}>
      {children}
    </ScholarshipContext.Provider>
  );
};

export const useScholarships = () => {
  const context = useContext(ScholarshipContext);
  if (context === undefined) {
    throw new Error('useScholarships must be used within a ScholarshipProvider');
  }
  return context;
};
